import { CAMPAIGN_FORM_TITLE } from '~/constants/campaign'
import { ReviewSection, ReviewTarget, FormatVersion } from '~/generated/graphql'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { Controller, useFormContext } from 'react-hook-form'
import { TextField } from '@mui/material'
import { URL_VALIDATION_REGEXP } from '~/lib/constants'
import { z } from 'zod'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

export const snsPostRedirectUrlSchemaFields = {
  snsPostRedirectUrl: z.string().max(1024, 'URLは1024文字以内で入力してください。'),
  instagramStoriesEnabled: z.boolean(),
}

export const snsPostRedirectUrlSchema = z.object({
  ...snsPostRedirectUrlSchemaFields,
})
export function validateSnsPostRedirectUrl<T extends z.output<typeof snsPostRedirectUrlSchema>>(
  v: T,
  ctx: z.RefinementCtx,
) {
  // InstagramのSNS投稿タイプが選択されている場合、URLのバリデーションを行う
  if (v.instagramStoriesEnabled) {
    if (!v.snsPostRedirectUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'SNSのURLは必須です。',
        path: ['snsPostRedirectUrl'],
      })
      return z.NEVER
    }
    const isValid = URL_VALIDATION_REGEXP.test(v.snsPostRedirectUrl)
    if (!isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'SNSのURLが正しくありません。',
        path: ['snsPostRedirectUrl'],
      })
      return z.NEVER
    }
  }
  return v
}

type Props = CampaignInputComponentProps

export const SnsPostRedirectUrl = ({ description }: Props) => {
  const { control, getValues } = useFormContext<z.infer<typeof snsPostRedirectUrlSchema>>()
  const instagramStoriesEnabled = getValues(`instagramStoriesEnabled`)
  if (!instagramStoriesEnabled) return null
  return (
    <CampaignInputComponent
      title={CAMPAIGN_FORM_TITLE.snsPostRedirectUrl}
      description={description}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.SnsPostRedirectUrl, ReviewTarget.LegalSnsPostRedirectUrl],
      }}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      <Controller
        name="snsPostRedirectUrl"
        render={({ field: { ref, ...field }, fieldState }) => (
          <TextField
            fullWidth
            placeholder="https://marketing.toridori.me"
            error={!!fieldState.error}
            helperText={<ValidationErrorMessage error={fieldState.error?.message} />}
            inputRef={ref}
            {...field}
          />
        )}
        control={control}
      />
    </CampaignInputComponent>
  )
}
